import { formatCPF } from '@brazilian-utils/brazilian-utils';
import {
  Alert,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Button as MuiButton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { downloadCSV, useNotify, useRecordContext, useRefresh } from 'react-admin';
import * as XLSX from 'xlsx';
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';
import { CPF_SHEET_TEMPLATE, CpfSheetRow, csvToMatrix, decodeCsv, parseCpfSheet } from '../../lib/parseCpfSheet';
import { TagType } from '../../models';

// Resposta de POST /chains/{chainId}/tag/{id}/import-cpfs (pontuax-backend src/api/tag/cpf-import.ts).
interface ImportResult {
  created: number;
  tagged: number;
  alreadyTagged: number;
  invalid: Array<{ line: number; cpf: string; reason: 'cpf_invalido' | 'repetido' }>;
  toRemove: Array<{ customerId: string; name: string | null; cpf: string }>; // até 20.000
  toRemoveTotal: number;
  removed: number;
}

const REASON_LABEL = { cpf_invalido: 'CPF inválido', repetido: 'Repetido' };

const plural = (n: number, singular: string, pluralText: string) => `${n} ${n === 1 ? singular : pluralText}`;

const readSheet = async (file: File): Promise<unknown[][]> => {
  if (file.name.toLowerCase().endsWith('.csv')) { return csvToMatrix(decodeCsv(await file.arrayBuffer())); }
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  return XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: '' });
};

const LIST_LIMIT = 200;

// Mostra só os primeiros: a etiqueta pode ter dezenas de milhares de membros.
const ScrollList: React.FC<{ items: string[]; total?: number }> = ({ items, total = items.length }) => (
  <Box sx={{ maxHeight: 160, overflowY: 'auto', mt: 1, pl: 1 }}>
    {items.slice(0, LIST_LIMIT).map(item => <Typography key={item} variant="body2">{item}</Typography>)}
    {total > LIST_LIMIT && <Typography variant="body2" color="text.secondary">e mais {total - LIST_LIMIT}</Typography>}
  </Box>
);

export const ImportCpfsDialog: React.FC = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<CpfSheetRow[]>([]);
  const [preview, setPreview] = useState<ImportResult | null>(null);
  const [remove, setRemove] = useState(false);

  if (!record || record.type !== TagType.client) { return null; }

  const closeDialog = () => {
    setOpen(false);
    setRows([]);
    setPreview(null);
    setRemove(false);
  };

  const send = async (body: object): Promise<ImportResult> => {
    const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/chains/${record.chainId}/tag/${record.id}/import-cpfs`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return json?.data?.[0] ?? json;
  };

  const run = async (action: () => Promise<void>) => {
    setLoading(true);
    try {
      await action();
    } catch (err: any) {
      notify(err?.body?.message || err?.message || 'Erro ao importar CPFs', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // deixa escolher o mesmo arquivo de novo
    if (!file) { return; }
    run(async () => {
      const parsed = parseCpfSheet(await readSheet(file));
      setPreview(null);
      setRemove(false);
      if (!parsed.length) {
        setRows([]);
        notify('Nenhuma linha encontrada no arquivo', { type: 'warning' });
        return;
      }
      setRows(parsed);
      setPreview(await send({ rows: parsed, dryRun: true }));
    });
  };

  const confirm = () => run(async () => {
    const removeCustomerIds = remove && preview ? preview.toRemove.map(r => r.customerId) : [];
    const result = await send({ rows, dryRun: false, removeCustomerIds });
    notify(
      `Importação concluída: ${plural(result.created, 'pré-cadastro novo', 'pré-cadastros novos')}, `
      + `${plural(result.tagged, 'entrou', 'entraram')} na etiqueta, ${plural(result.removed, 'removido', 'removidos')}.`,
      { type: 'success' },
    );
    refresh();
    closeDialog();
  });

  const noValid = !!preview && preview.created + preview.tagged + preview.alreadyTagged === 0;

  return (
    <Box sx={{ my: 1 }}>
      <MuiButton variant="outlined" onClick={() => setOpen(true)}>
        Importar CPFs
      </MuiButton>

      <Dialog open={open} onClose={loading ? undefined : closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Importar CPFs na etiqueta</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Arquivo .csv, .xlsx ou .xls com a coluna CPF, com ou sem pontuação. Nome é opcional. Até 20.000 linhas.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <MuiButton component="label" variant="outlined" disabled={loading}>
              Escolher arquivo
              <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={onFile} />
            </MuiButton>
            <MuiButton onClick={() => downloadCSV(CPF_SHEET_TEMPLATE, 'modelo-cpfs-etiqueta')}>
              Baixar modelo
            </MuiButton>
          </Box>

          {preview && (
            <Alert severity={noValid ? 'warning' : 'info'} sx={{ mt: 2 }}>
              <strong>Prévia, nada foi gravado.</strong>
              {noValid ? (
                <Typography variant="body2">Nenhum CPF válido no arquivo.</Typography>
              ) : (
                <>
                  <Typography variant="body2">{plural(preview.created, 'pré-cadastro novo', 'pré-cadastros novos')}</Typography>
                  <Typography variant="body2">
                    {plural(preview.tagged - preview.created, 'cliente já cadastrado entra', 'clientes já cadastrados entram')}
                  </Typography>
                  <Typography variant="body2">{plural(preview.alreadyTagged, 'já estava na etiqueta', 'já estavam na etiqueta')}</Typography>
                </>
              )}
              {preview.invalid.length > 0 && (
                <>
                  <Typography variant="body2">{plural(preview.invalid.length, 'linha ignorada', 'linhas ignoradas')}:</Typography>
                  <ScrollList items={preview.invalid.map(i => `Linha ${i.line}: ${i.cpf || '(vazio)'} (${REASON_LABEL[i.reason]})`)} />
                </>
              )}
            </Alert>
          )}

          {preview && !noValid && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Quem ainda não se cadastrou entra como pré-cadastro e continua na etiqueta quando se cadastrar no app.
              Pré-cadastro fica sem celular, então SMS para esta etiqueta não chega nele.
            </Typography>
          )}

          {preview && preview.toRemove.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={remove} onChange={event => setRemove(event.target.checked)} />}
                label={`Remover da etiqueta ${plural(preview.toRemove.length, 'pessoa que não está', 'pessoas que não estão')} nesta lista`}
              />
              {preview.toRemoveTotal > preview.toRemove.length && (
                <Typography variant="body2" color="text.secondary">
                  {preview.toRemoveTotal} pessoas não estão na lista. Esta importação remove as primeiras {preview.toRemove.length}; importe de novo para remover o resto.
                </Typography>
              )}
              <ScrollList items={preview.toRemove.map(r => `${r.name || 'Sem nome'} · ${formatCPF(r.cpf)}`)} total={preview.toRemoveTotal} />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <MuiButton onClick={closeDialog} disabled={loading}>
            Cancelar
          </MuiButton>
          <MuiButton variant="contained" onClick={confirm} disabled={loading || !preview || noValid}>
            {loading ? 'Aguarde…' : 'Confirmar'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImportCpfsDialog;
